<template>
  <div class="flex flex-col">
    <div class="text-static-default-hi" data-qa="productCardPrice">
      <p
        v-if="dueTodayPrice"
        class="label-medium-bold"
        :class="{ 'text-static-success-hi': !!deal }"
      >
        {{ dueTodayPrice }}
      </p>
      <p
        class="label-medium-bold"
        :class="{ 'text-static-success-hi': !!deal }"
      >
        <span class="sr-only">{{ i18n(translations.priceSRLabel) }}</span>
        <ProductPriceHighlighted :price="highlightedPrice" />
        {{ highlightedPriceLabel }}
      </p>
      <div class="space-y-4">
        <p v-if="dealInfo" class="label-medium-bold text-static-success-hi">
          {{ dealInfo }}
          <RevTooltip v-if="deal?.type === 'flash_sales'">
            <template #trigger="{ show, hide }">
              <IconInfo
                :aria-label="i18n(translations.moreInfoSRLabel)"
                class="relative inline align-text-bottom"
                size="16"
                @focusin="show"
                @focusout="hide"
                @mouseleave="hide"
                @mouseover="show"
              />
            </template>
            {{ i18n(translations.flashSalesDiscountTooltip) }}
          </RevTooltip>
        </p>
        <p v-if="lastLowestPrice" class="label-medium text-static-default-low">
          {{ lastLowestPrice }}
          <RevTooltip v-if="sellerFlashSalesTooltip">
            <template #trigger="{ show, hide }">
              <IconInfo
                :aria-label="i18n(translations.moreInfoSRLabel)"
                class="relative inline align-top"
                size="16"
                @focusin="show"
                @focusout="hide"
                @mouseleave="hide"
                @mouseover="show"
              />
            </template>
            {{ sellerFlashSalesTooltip }}
          </RevTooltip>
        </p>
        <p
          v-else-if="
            newPrice && !(afterTradeinPrice && deal?.type === 'flash_sales')
          "
          class="label-medium text-static-default-low"
        >
          <span aria-hidden="true">
            <s>{{ newPrice }}</s>
            {{ i18n(translations.newKeyword) }}
          </span>
          <span class="sr-only">{{
            i18n(translations.newPriceSRLabel, { price: newPrice })
          }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Deal } from '@backmarket/nuxt-layer-cms/products/algolia/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { calculateReduction } from '@backmarket/utils/math/calculateReduction'
import { parseFormattedPrice } from '@backmarket/utils/math/parseFormattedPrice'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'
import { addWeeks, isMonday, set, startOfWeek, subWeeks } from 'date-fns'

import translations from './ProductPrice.translations'
import ProductPriceHighlighted from './ProductPriceHighlighted.vue'

const { price, newPrice, afterTradeinPrice, deal } = defineProps<{
  price: string
  newPrice?: string
  afterTradeinPrice?: string
  deal?: Deal
}>()

const i18n = useI18n()
const locale = useI18nLocale()

const parsedBasePrice = computed(() => parseFormattedPrice(price))

const highlightedPrice = computed(() => {
  const activePrice = afterTradeinPrice ?? price
  if (deal?.type !== 'flash_sales') return activePrice

  const parsedActivePrice = parseFormattedPrice(activePrice)
  if (!parsedActivePrice) return activePrice

  const discountedPrice = calculateReduction(parsedActivePrice, deal.discount)

  if (!discountedPrice) return activePrice

  const clampedAmount = Math.max(0, parseFloat(discountedPrice.amount))

  return i18n.price({ ...discountedPrice, amount: clampedAmount.toString() })
})

const highlightedPriceLabel = computed(() => {
  if (afterTradeinPrice) return i18n(translations.afterTradin)

  return null
})

const dueTodayPrice = computed(() => {
  if (!afterTradeinPrice) return null

  if (deal?.type === 'flash_sales') {
    if (!parsedBasePrice.value) return null

    const reduction = calculateReduction(parsedBasePrice.value, deal.discount)

    return reduction
      ? `${i18n.price(reduction)} ${i18n(translations.dueToday)}`
      : null
  }

  return `${price} ${i18n(translations.dueToday)}`
})

const dealInfo = computed(() => {
  if (deal?.type === 'flash_sales') {
    return i18n(translations.flashSalesDiscount, {
      discount: `${i18n.price(deal.discount, {
        trailingZeroDisplay: 'stripIfInteger',
      })}`,
    })
  }
  if (deal?.type === 'seller_flash_sales' && !afterTradeinPrice) {
    if (!parsedBasePrice.value) return null

    const reduction = calculateReduction(
      deal.percentage.minimum_price,
      parsedBasePrice.value,
    )

    return reduction
      ? i18n(translations.sellerFlashSalesDiscount, {
          price: i18n.price(reduction),
        })
      : null
  }

  return null
})

const lastLowestPrice = computed(() => {
  if (deal?.type === 'seller_flash_sales') {
    return i18n(translations.lastLowestPrice, {
      price: i18n.price(deal.percentage.minimum_price),
    })
  }

  return null
})

const sellerFlashSalesTooltip = computed(() => {
  if (deal?.type !== 'seller_flash_sales') return null
  const now = new Date()
  const mondayThisWeek = set(startOfWeek(now, { weekStartsOn: 1 }), {
    hours: 12,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  })

  // If it's Monday before 12pm, the current week hasn't started yet
  const startDate =
    isMonday(now) && now.getHours() < 12
      ? subWeeks(mondayThisWeek, 1)
      : mondayThisWeek

  const endDate = addWeeks(startDate, 1)

  return i18n(translations.sellerFlashSalesDiscountTooltip, {
    start_date: startDate.toLocaleDateString(locale, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }),
    end_date: endDate.toLocaleDateString(locale, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }),
  })
})
</script>
